import React from 'react';
import { TranslationProvider } from './common/i18n/TranslationProvider';
import { theme } from './AppTheme';
import ErrorBoundary from './common/errors/ErrorBoundary';
import { Route, Routes, BrowserRouter } from 'react-router-dom';
import { MessagesProvider } from './components/messages/MessagesProvider';
import routes, { RouteConfigEx } from './routes/routeConfig';
import Frame from './components/Frame';
import { CacheProvider, ThemeProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { CssBaseline } from '@mui/material';
import { BackdropProvider } from './components/contexts/BackDropProvider';
import { LandingPageModeProvider } from './components/contexts/LandingPageModeProvider';
import { ContentRefProvider } from './components/contexts/ContentRefProvider';
import { BreadcrumbsProvider } from './components/contexts/BreadcrumbsProvider';
import { JwtCreationProvider } from './components/contexts/JwtCreationContext';
import { LoadingProvider } from './components/contexts/LoadingContext';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers';
import de from 'date-fns/locale/de';
import { LicenseInfo } from '@mui/x-license';
import { ClientCompanyRegistrationContextProvider } from './components/client/companyRegistration/context/ClientCompanyRegistrationContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RetryRequestProvider } from './components/contexts/RetryRequestContext';
import AdminAutoRedirect from './components/LandingPage/AdminLoginRedirect';

//https://mui.com/x/introduction/licensing/#license-key-security
LicenseInfo.setLicenseKey(
    '14a51681c1e25ccef04ac1e54fd7dcd9Tz05NDg1MSxFPTE4MTcyMTIzODAwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
);

export const muiCache = createCache({
    key: 'mui',
    prepend: true,
});

function BeamApp(): React.ReactElement {
    // Create a React Query client
    const queryClient = new QueryClient();

    function renderRoutes(routes: RouteConfigEx[], parentKey?: number) {
        return routes.map((route, i) => (
            <Route
                key={parentKey ? i.toString() + parentKey.toString() : i.toString()}
                path={route.path}
                element={route.getComponent()}
            >
                {route.routes && renderRoutes(route.routes)}
            </Route>
        ));
    }

    return (
        <CacheProvider value={muiCache}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={theme}>
                    <CssBaseline>
                        <TranslationProvider>
                            <MessagesProvider>
                                <ErrorBoundary>
                                    <JwtCreationProvider>
                                        <BrowserRouter>
                                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                                                <BackdropProvider>
                                                    <RetryRequestProvider>
                                                        <LoadingProvider>
                                                            <LandingPageModeProvider>
                                                                <BreadcrumbsProvider>
                                                                    <ContentRefProvider>
                                                                        <ClientCompanyRegistrationContextProvider>
                                                                            <Frame>
                                                                                <AdminAutoRedirect />
                                                                                <Routes>{renderRoutes(routes)}</Routes>
                                                                            </Frame>
                                                                        </ClientCompanyRegistrationContextProvider>
                                                                    </ContentRefProvider>
                                                                </BreadcrumbsProvider>
                                                            </LandingPageModeProvider>
                                                        </LoadingProvider>
                                                    </RetryRequestProvider>
                                                </BackdropProvider>
                                            </LocalizationProvider>
                                        </BrowserRouter>
                                    </JwtCreationProvider>
                                </ErrorBoundary>
                            </MessagesProvider>
                        </TranslationProvider>
                    </CssBaseline>
                </ThemeProvider>
            </QueryClientProvider>
        </CacheProvider>
    );
}
export default BeamApp;
